'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api/client';
import { useToast } from '@/components/ui';
import {
  getNotificationPermission,
  onForegroundMessage,
  requestFcmToken,
  type PushPermission,
} from '@/lib/firebase/client';

async function registerToken(token: string): Promise<void> {
  await api.post('/api/notifications/push/register', {
    token,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  });
}

export type UsePushNotifications = {
  permission: PushPermission;
  enabling: boolean;
  /** Prompts for permission (if needed) and registers the device token. */
  enablePush: () => Promise<boolean>;
};

/**
 * Wires Firebase Cloud Messaging for the admin panel:
 * - toast for foreground messages
 * - silent re-register when permission is already granted
 * - enablePush() for an explicit user gesture
 */
export function usePushNotifications(enabled: boolean): UsePushNotifications {
  const { toast } = useToast();
  const [permission, setPermission] = useState<PushPermission>('unsupported');
  const [enabling, setEnabling] = useState(false);
  const registeredRef = useRef(false);

  const enablePush = useCallback(async (): Promise<boolean> => {
    setEnabling(true);
    try {
      const token = await requestFcmToken();
      setPermission(await getNotificationPermission());
      if (!token) return false;
      await registerToken(token);
      registeredRef.current = true;
      return true;
    } catch (err) {
      console.error('[fcm] enablePush failed', err);
      return false;
    } finally {
      setEnabling(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let unsubscribe = () => {};
    let cancelled = false;

    (async () => {
      const current = await getNotificationPermission();
      if (cancelled) return;
      setPermission(current);

      // Auto-register when already granted. For 'default', wait for a user
      // gesture (enablePush) — browsers block permission prompts without one.
      if (current === 'granted' && !registeredRef.current) {
        const token = await requestFcmToken();
        if (cancelled) return;
        setPermission(await getNotificationPermission());
        if (token) {
          try {
            await registerToken(token);
            registeredRef.current = true;
          } catch (err) {
            console.error('[fcm] silent token register failed', err);
          }
        }
      }

      unsubscribe = await onForegroundMessage(payload => {
        const title = payload.notification?.title ?? payload.data?.title ?? 'Notification';
        const body = payload.notification?.body ?? payload.data?.body;
        toast(body ? `${title}: ${body}` : title, 'info');
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [enabled, toast]);

  return { permission, enabling, enablePush };
}
